import { KeyedSet } from "./set";

export function chunks<T>(iterable: Iterable<T>, size: number): Iterable<T[]> {
  return {
    *[Symbol.iterator]() {
      let result: T[] = [];
      for (const item of iterable) {
        if (size <= result.push(item)) {
          yield result;
          result = [];
        }
      }
      if (result.length) {
        yield result;
      }
    },
  };
}

export function iterableConcat<T>(
  iterables: Iterable<Iterable<T>>,
): Iterable<T> {
  return {
    *[Symbol.iterator]() {
      for (const iterable of iterables) {
        yield* iterable;
      }
    },
  };
}

export function iterableEntries<T>(
  iterable: Iterable<T>,
): Iterable<[number, T]> {
  return {
    *[Symbol.iterator]() {
      let index = 0;
      for (const element of iterable) {
        yield [index++, element];
      }
    },
  };
}

export function iterableEvery<T>(
  iterable: Iterable<T>,
  fn: (element: T, index: number) => boolean,
) {
  let index = 0;
  for (const element of iterable) {
    if (!fn(element, index++)) {
      return false;
    }
  }
  return true;
}

export function iterableFilter<A>(
  iterable: Iterable<A>,
  fn: (element: A, index: number) => boolean,
): Iterable<A> {
  return {
    *[Symbol.iterator]() {
      let index = 0;
      for (const element of iterable) {
        if (fn(element, index++)) {
          yield element;
        }
      }
    },
  };
}

export function iterableFlatMap<A, B>(
  iterable: Iterable<A>,
  fn: (element: A, index: number) => Iterable<B>,
): Iterable<B> {
  return {
    *[Symbol.iterator]() {
      let index = 0;
      for (const element of iterable) {
        yield* fn(element, index++);
      }
    },
  };
}

export function iterableJoin<T>(
  iterable: Iterable<T>,
  separator: T,
): Iterable<T> {
  return {
    *[Symbol.iterator]() {
      let first = true;
      for (const item of iterable) {
        if (first) {
          first = false;
        } else {
          yield separator;
        }
        yield item;
      }
    },
  };
}

export function iterableMap<A, B>(
  iterable: Iterable<A>,
  fn: (element: A, index: number) => B,
): Iterable<B> {
  return {
    *[Symbol.iterator]() {
      let index = 0;
      for (const element of iterable) {
        yield fn(element, index++);
      }
    },
  };
}

// @ts-expect-error unused-export-debt
function iterableSlice<T>(
  iterable: Iterable<T>,
  start: number,
  end?: number,
): Iterable<T> {
  return {
    *[Symbol.iterator]() {
      let index = 0;
      for (const element of iterable) {
        if (start <= index && (end === undefined || index < end)) {
          yield element;
        }
        index++;
      }
    },
  };
}

// @ts-expect-error unused-export-debt
function iterableSome<T>(
  iterable: Iterable<T>,
  fn: (element: T, index: number) => boolean,
) {
  let index = 0;
  for (const element of iterable) {
    if (fn(element, index++)) {
      return true;
    }
  }
  return false;
}

// @ts-expect-error unused-export-debt
function iterableZip<T extends any[]>(
  ...iterables: { [K in keyof T]: Iterable<T[K]> }
): Iterable<T> {
  return {
    *[Symbol.iterator]() {
      const iterators = iterables.map((iterable) =>
        iterable[Symbol.iterator](),
      );
      try {
        loop: while (true) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const result: T = [] as any;
          for (const iterator of iterators) {
            const { value, done } = iterator.next();
            if (done) {
              break loop;
            }
            result.push(value);
          }
          yield result;
        }
      } finally {
        for (const iterator of iterators) {
          iterator.return?.();
        }
      }
    },
  };
}

export function unique<T>(
  items: Iterable<T>,
  uniqueKey?: (item: T) => string,
): Iterable<T> {
  if (!uniqueKey) {
    return new Set(items);
  }
  return {
    *[Symbol.iterator]() {
      const seen = new KeyedSet(uniqueKey);
      for (const item of items) {
        if (!seen.has(item)) {
          seen.add(item);
          yield item;
        }
      }
    },
  };
}
